import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Cell } from 'recharts';
import { Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const RMCumulativePayback = ({ annualSummary, monthlySchedule, cumulativePayback, paybackDate, isLoading, error }) => {
  console.log('RMCumulativePayback props:', { annualSummary, monthlySchedule, cumulativePayback, paybackDate, isLoading, error });
  const axisTickStyle = { fill: 'hsl(var(--chart-axis))', fontSize: 12 };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2 text-foreground">Calculating Cumulative Payback...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative rounded-xl border border-destructive/45 bg-destructive/15 px-4 py-3 text-destructive" role="alert">
        <strong className="font-bold">Error:</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  if (!monthlySchedule || monthlySchedule.length === 0) {
    return <div className="text-foreground">No data available for Cumulative Payback Analysis.</div>;
  }

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'MMM yyyy');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatPaybackPeriod = (years) => {
    const wholeYears = Math.floor(years);
    const months = Math.round((years - wholeYears) * 12);
    
    let result = '';
    if (wholeYears > 0) {
      result += `${wholeYears} year${wholeYears !== 1 ? 's' : ''}`;
    }
    if (months > 0) {
      if (result) result += ' and ';
      result += `${months} month${months !== 1 ? 's' : ''}`;
    }
    return result || 'Less than a month';
  };

  const calculatePaybackDate = () => {
    if (paybackDate) return formatDate(paybackDate);
    if (!monthlySchedule || monthlySchedule.length === 0) return null;

    const paybackMonth = monthlySchedule.find(month => month.cumulative_profit > 0);
    return paybackMonth ? formatDate(paybackMonth.index) : null;
  };

  const getBarColor = (value) => {
    const maxValue = Math.max(...monthlySchedule.map(item => Math.abs(item.cumulative_profit)));
    const intensity = Math.abs(value) / maxValue;

    if (value >= 0) {
      return `hsl(var(--chart-up) / ${Math.min(0.95, 0.42 + intensity * 0.48)})`;
    }
    return `hsl(var(--chart-down) / ${Math.min(0.95, 0.42 + intensity * 0.48)})`;
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const barColor = getBarColor(payload[0].value);
      return (
        <div className="ace-tooltip p-4">
          <p className="font-bold" style={{ color: barColor }}>{formatDate(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: barColor }}>
              {`${entry.name}: ${formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderContent = () => {
    if (!monthlySchedule || monthlySchedule.length === 0) {
      return <p className="text-foreground">No data available for Cumulative Payback Analysis.</p>;
    }

    const paybackDateFormatted = calculatePaybackDate();

    return (
      <>
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-semibold text-primary">Cumulative Payback Period</h3>
          <p className="mb-2 text-sm text-muted-foreground">
            The time it takes for the cumulative profit to become positive, indicating when the investment breaks even.
          </p>
          <p className="text-2xl font-bold text-foreground">
            {cumulativePayback !== null && cumulativePayback !== undefined
              ? formatPaybackPeriod(cumulativePayback)
              : 'Not reached within the projection period'}
          </p>
          {paybackDateFormatted && (
            <p className="mt-1 text-sm text-muted-foreground">
              Payback achieved on {paybackDateFormatted}
            </p>
          )}
        </div>
        <div>
          <h3 className="mb-2 text-lg font-semibold text-primary">Cumulative Profit Over Time</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={monthlySchedule}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
              <XAxis 
                dataKey="index" 
                stroke="hsl(var(--chart-axis))"
                tick={axisTickStyle}
                tickFormatter={formatDate}
                interval={3}
                angle={-45}
                textAnchor="end"
                height={70}
              />
              <YAxis stroke="hsl(var(--chart-axis))" tick={axisTickStyle} tickFormatter={formatCurrency} />
              <ChartTooltip content={customTooltip} />
              <Bar dataKey="cumulative_profit" name="Cumulative Profit">
                {monthlySchedule.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.cumulative_profit)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  return (
    <Card className="card-wrapper mt-6">
      <CardHeader>
        <CardTitle className="!text-primary">Cumulative Payback Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default RMCumulativePayback;